import { Dispatch, SetStateAction, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormAutocompleteController, FormInputNumberController } from '@/components';
import {
  Ingredient,
  IngredientWithAmount,
  onQueryError,
  onQuerySuccess,
  ProductIngredientUpdateForm,
  useIngredients,
  useProduct,
} from '@/core';

import { styles } from './styles';

type FormIngredient = {
  id: number;
  name: string;
  unit: string;
};

type IngredientFormData = {
  ingredient: FormIngredient;
  amount: number;
};

const schema = z.object({
  ingredient: z.object({
    id: z.number(),
    name: z.string(),
    unit: z.string(),
  }),
  amount: z.number(),
});

export const EditableIngredient = ({
  ingredientWithAmount,
  setIngredientsWithAmount,
}: {
  ingredientWithAmount: IngredientWithAmount;
  setIngredientsWithAmount: Dispatch<SetStateAction<IngredientWithAmount[]>>;
}) => {
  const { t } = useTranslation();
  const { useIngredientsSearch } = useIngredients();
  const {
    queries: { useProductIngredients },
    mutations: { updateIngredientsMutation, deleteProductIngredientMutation },
  } = useProduct();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [options, setOptions] = useState<Ingredient[]>([]);
  const { data: ingredientsResponse } = useProductIngredients(Number(id), true);

  const methods = useForm<IngredientFormData>({
    defaultValues: {
      ingredient: ingredientWithAmount.ingredient,
      amount: ingredientWithAmount.amount,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { getValues } = methods;

  const onSuccess = () => {
    onQuerySuccess({
      queryClient,
      queryKeysToInvalidate: [['productIngredients', id]],
    });
  };

  const handleDeleteIngredientClick = () => {
    deleteProductIngredientMutation.mutate(
      {
        productId: Number(id),
        ingredientId: ingredientWithAmount.ingredient.id,
      },
      {
        onSuccess: () => {
          setIngredientsWithAmount((prev) =>
            prev.filter((i) => i.ingredient.id !== ingredientWithAmount.ingredient.id),
          );
          onSuccess();
        },
        onError: () => {
          onQueryError({ errorMessage: t('INGREDIENT.DELETE_FAILURE') });
        },
      },
    );
  };

  const handleSaveEditableIngredient = () => {
    const values = getValues();
    if (!values.ingredient.id) return;
    setIngredientsWithAmount((prev: IngredientWithAmount[]) => {
      const updatedIngredients = prev.map((item) => {
        if (item.ingredient.id === ingredientWithAmount.ingredient.id) {
          return {
            ingredient: {
              ...values.ingredient,
              description: '',
              price: '0',
              is_active: true,
            },
            amount: Number(values.amount),
          };
        }
        return item;
      });

      const updatedIngredientsApiForm: ProductIngredientUpdateForm[] = updatedIngredients
        .filter((i) => i.ingredient !== undefined)
        .map((item) => ({
          ingredient_id: item.ingredient!.id,
          amount: Number(item.amount.toFixed(2)),
        }));

      updateIngredientsMutation.mutate(
        {
          productId: Number(id),
          data: updatedIngredientsApiForm,
        },
        { onSuccess },
      );

      return updatedIngredients;
    });
  };

  return (
    <FormProvider {...methods}>
      <TableRow>
        <TableCell align="left">
          <FormAutocompleteController<Ingredient>
            label={t('INGREDIENT.CHOOSE')}
            controllerName={'ingredient'}
            options={options}
            fetchOptions={useIngredientsSearch}
            setOptions={setOptions}
            getOptionLabel={(option) => option.name || ''}
            getOptionKey={(option) => option.id.toString()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fieldOnBlur={handleSaveEditableIngredient}
            renderInput={() => <div></div>}
            optionsToFilterOut={ingredientsResponse?.data?.map((i) => i.ingredient) || []}
          />
        </TableCell>
        <TableCell align="left">
          <FormInputNumberController
            label={''}
            controllerName={'amount'}
            fieldOnBlur={handleSaveEditableIngredient}
            decimalPlaces={2}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={styles.shrinkCell}>
            {getValues()?.ingredient?.unit || ''}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <IconButton size="medium" onClick={handleDeleteIngredientClick}>
            <span className="material-symbols-rounded">close</span>
          </IconButton>
        </TableCell>
      </TableRow>
    </FormProvider>
  );
};
